/**
 * Olven — Scroll Reveal & Animation Engine
 * Lightweight, no dependencies, respects prefers-reduced-motion.
 */

(function () {
  'use strict';

  // Skip all animations if user prefers reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  // Observe elements with data-olven-reveal, data-olven-fade or data-olven-mask.
  const revealTargets = document.querySelectorAll('[data-olven-reveal], [data-olven-fade], [data-olven-mask]');

  if (!revealTargets.length) return;

  // Hidden pre-reveal states only apply once this engine is confirmed running —
  // if this file ever fails to load, content simply renders visible.
  document.documentElement.classList.add('olven-anim');

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target); // animate once only
        }
      });
    },
    {
      threshold: 0.12,
      rootMargin: '0px 0px -40px 0px',
    }
  );

  revealTargets.forEach((el) => observer.observe(el));
})();


/**
 * Olven — Docked header (always visible).
 * #header-group is CSS-sticky with a negative top equal to everything above the
 * header (the announcement bar), so the announcement scrolls away and the header
 * docks at the top for the whole page. Scroll state (.is-scrolled → translucent)
 * comes from an IntersectionObserver sentinel, not window.scrollY: Horizon scrolls
 * .page-wrapper on desktop, so the window never moves there.
 * If this script fails, the CSS fallback (top: 0) keeps the whole group docked.
 */
(function () {
  'use strict';
  var root = document.documentElement;
  var io = null, ro = null, sentinel = null;

  function init() {
    var header = document.querySelector('[data-olven-header]');
    var group = document.getElementById('header-group');
    if (io) { io.disconnect(); io = null; }
    if (ro) ro.disconnect();
    if (!header || !group) return;

    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.className = 'olven-header-sentinel';
    }
    if (sentinel.nextElementSibling !== group) group.parentNode.insertBefore(sentinel, group);

    // With a full-bleed hero, the header keeps its translucent rest state until the hero
    // has scrolled out from beneath it; otherwise it switches just after docking.
    var hero = document.querySelector('.olven-hero--bg, .olven-scihero--bg');
    var observedHeight = -1;

    function observe() {
      if (io) io.disconnect();
      observedHeight = header.offsetHeight;
      io = new IntersectionObserver(function (entries) {
        header.classList.toggle('is-scrolled', !entries[0].isIntersecting);
      }, hero ? { rootMargin: '-' + observedHeight + 'px 0px 0px 0px' } : undefined);
      io.observe(hero || sentinel);
    }

    function measure() {
      var above = Math.max(0, Math.round(header.getBoundingClientRect().top - group.getBoundingClientRect().top));
      root.style.setProperty('--olven-header-dock', above + 'px');
      root.style.setProperty('--sticky-header-offset', header.offsetHeight + 'px');
      // The sentinel leaves the viewport just after the header docks.
      var h = above + 4;
      sentinel.style.height = h + 'px';
      sentinel.style.marginBottom = -h + 'px';
      if (hero && header.offsetHeight !== observedHeight) observe();
    }
    measure();
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(measure);
      ro.observe(group);
    }
    if (!io) observe();
  }

  init();
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector && e.target.querySelector('[data-olven-header]')) init();
  });
})();


/**
 * Olven — Sticky scrollytelling active-step engine.
 * Used by the FAQ page category index. (The former count-up block was removed
 * with the Science-page rebuild — no data-countup elements exist any more.)
 */
(function () {
  'use strict';

  /* --- Sticky scrollytelling: highlight the active step --- */
  var story = document.querySelector('[data-olven-story]');
  if (story) {
    var steps = story.querySelectorAll('[data-step]');
    var pin = story.querySelector('[data-story-pin]');
    var stepObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        steps.forEach(function (s) { s.classList.remove('is-active'); });
        e.target.classList.add('is-active');
        if (pin) pin.setAttribute('data-active', e.target.getAttribute('data-step'));
      });
    }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
    steps.forEach(function (s) { stepObs.observe(s); });
    if (steps[0]) { steps[0].classList.add('is-active'); if (pin) pin.setAttribute('data-active', steps[0].getAttribute('data-step')); }
  }
})();


/**
 * Olven — Cinematic motion layer (Phase 20b)
 * 1) Word-by-word hero headline reveal
 * 2) Gold reading-progress bar
 * 3) Scroll-linked image parallax drift
 * No libraries. One shared rAF scroll loop. Respects prefers-reduced-motion.
 */
(function () {
  'use strict';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- 1. Word-by-word headline reveal ---- */
  if (!reduce) {
    // FAQ + Contact heroes only. The homepage hero, dark statement and Science
    // hero are GSAP/SplitText-owned (src/motion/) — never both systems.
    var heads = document.querySelectorAll(
      '.olven-fqhero__heading, .olven-cthero__heading'
    );
    heads.forEach(function (el) {
      if (el.dataset.olvenWords) return;
      // only split elements that contain plain text (no child elements)
      if (el.childElementCount !== 0) return;
      var words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      words.forEach(function (w, i) {
        var wrap = document.createElement('span');
        wrap.className = 'olven-word';
        var inner = document.createElement('span');
        inner.className = 'olven-word__inner';
        inner.textContent = w;
        inner.style.transitionDelay = (120 + i * 70) + 'ms';
        wrap.appendChild(inner);
        el.appendChild(wrap);
        el.appendChild(document.createTextNode(' '));
      });
      el.dataset.olvenWords = '1';
      // ensure the parent reveal doesn't double-hide the words
      el.removeAttribute('data-olven-reveal');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { el.classList.add('olven-words-in'); });
      });
    });
  }

  /* ---- 2. Reading-progress bar ---- */
  var bar = document.createElement('div');
  bar.className = 'olven-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);

  /* ---- 3. Parallax targets ---- */
  var pxTargets = [];
  if (!reduce) {
    // Hero image excluded: its parallax is GSAP-owned on the homepage.
    document.querySelectorAll(
      '.olven-showcase__image--photo, .olven-ingredients__img, .olven-objgallery__img--drift, .olven-sciformula__img'
    ).forEach(function (img) { pxTargets.push(img); });
  }

  /* ---- Shared scroll loop ---- */
  var ticking = false;
  function frame() {
    var y = window.scrollY;
    var max = document.documentElement.scrollHeight - window.innerHeight;
    bar.style.transform = 'scaleX(' + (max > 0 ? Math.min(y / max, 1) : 0) + ')';

    var vh = window.innerHeight;
    for (var i = 0; i < pxTargets.length; i++) {
      var r = pxTargets[i].getBoundingClientRect();
      if (r.bottom < -80 || r.top > vh + 80) continue;
      var offset = ((r.top + r.height / 2) - vh / 2) * -0.05;
      pxTargets[i].style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
    }
    ticking = false;
  }
  window.addEventListener('scroll', function () {
    if (!ticking) { ticking = true; requestAnimationFrame(frame); }
  }, { passive: true });
  frame();
})();
